import {
  Component,
  HostBinding,
  Input,
  OnInit,
  ViewChild,
  input,
  output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '@maximizer/core/shared/data-access';
import { ListItem } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StringValidator } from '@maximizer/core/shared/ui/validators';
import {
  NavigationService,
  OutlookService,
  OutlookSessionService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import {
  TaskForm,
  TaskModuleConfiguration,
  TaskPrefill,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TaskService } from '@maximizer/outlook/task/data-access';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { catchError, finalize, forkJoin } from 'rxjs';

@Component({
  selector: 'maximizer-add-task',
  templateUrl: './add-task.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DATEINPUTS,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class AddTaskComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  @ViewChild('notification')
  notification!: OutlookNotificationComponent;

  @Input() type!: 'lead' | 'abentry';

  @Input()
  set id(id: string) {
    if (this.type == 'lead') this._leadKey = decodeURI(id);
    if (this.type == 'abentry') this._abEntryKey = decodeURI(id);
  }
  get id() {
    return this._leadKey ?? this._abEntryKey ?? '';
  }

  embedded = input(false);
  prefill = input<TaskPrefill | null>(null);

  saved = output<void>();
  cancelled = output<void>();

  private readonly _now = new Date();
  private _abEntryKey: string | null = null;
  private _leadKey: string | null = null;

  form?: FormGroupWrapper<TaskForm>;
  configuration?: TaskModuleConfiguration;
  loading = true;

  noneValue: ListItem<string> = {
    id: '-1',
    name: this.translate.instant('outlook.forms.none'),
  };

  constructor(
    private readonly taskService: TaskService,
    private readonly session: OutlookSessionService,
    private readonly translate: TranslateService,
    private readonly userService: UserService,
    private readonly navigationService: NavigationService,
    private readonly outlookService: OutlookService,
    public outlookStore: OutlookStore,
  ) {}

  ngOnInit(): void {
    this.setForm();
    this.configuration = {
      options: {
        categories: [this.noneValue],
        results: [this.noneValue],
        priorities: [this.noneValue],
      },
      users: [],
    };

    this.form?.patch({
      priority: this.noneValue.id,
      category: this.noneValue.id,
      result: this.noneValue.id,
    });

    this.applyPrefill();

    if (this._leadKey || this._abEntryKey) this.loadData();
    if (!this.embedded()) this.populateSubject();
  }

  private applyPrefill(): void {
    const prefill = this.prefill();
    if (!prefill || !this.form) return;

    this.form.patch({
      ...(prefill.activity === null ? {} : { activity: prefill.activity }),
      ...(prefill.dueDate === null ? {} : { dueDate: prefill.dueDate }),
      ...(prefill.priority === null ? {} : { priority: prefill.priority }),
      ...(prefill.category === null ? {} : { category: prefill.category }),
    });
  }

  async populateSubject() {
    const subject = await this.outlookService.getSubject(true);

    this.form?.patch({
      activity: subject,
    });
  }

  transformToNullWhenNone(input: string | null) {
    if (input == this.noneValue.id) return null;
    return input;
  }

  save(): void {
    if (this.form) {
      this.form.patch({
        abEntryKey: this._abEntryKey,
        leadKey: this._leadKey,
        priority: this.transformToNullWhenNone(this.form.value.priority),
        category: this.transformToNullWhenNone(this.form.value.category),
        result: this.transformToNullWhenNone(this.form.value.result),
      });

      const validation = this.form.validate();
      if (!validation.valid) return;

      const priority = this.form.value.priority ?? this.noneValue.id;
      const category = this.form.value.category ?? this.noneValue.id;
      const result = this.form.value.result ?? this.noneValue.id;

      this.loading = true;
      this.taskService
        .create(this.form.value)
        .pipe(
          catchError(async () => {
            this.onSaveError(priority, category, result);
            this.notification.show('create', 'error');
          }),
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe((saveResult) => {
          if (saveResult == null) return;

          if (this.embedded()) {
            this.saved.emit();
            return;
          }
          this.notification.show('create', 'success');
          this.navigationService.navigateBack();
        });
    }
  }

  cancel(): void {
    if (this.embedded()) {
      this.cancelled.emit();
      return;
    }
    this.navigationService.navigateBack();
  }

  private onSaveError(priority: string, category: string, result: string) {
    this.form?.patch({
      priority: priority,
      category: category,
      result: result,
    });
  }

  private setForm(): void {
    this.form = FormWrapperBuilder.group<TaskForm>({
      key: null,
      dueDate: [this._now, [Validators.required]],
      activity: [
        null,
        {
          validators: Validators.compose([
            Validators.required,
            Validators.minLength(1),
            StringValidator.NotEmpty(),
          ]),
        },
      ],
      priority: '-1',
      category: '-1',
      result: '-1',
      assignedUser: [
        null,
        {
          validators: Validators.compose([
            Validators.required,
            Validators.minLength(1),
            StringValidator.NotEmpty(),
          ]),
        },
      ],
      abEntryKey: null,
      leadKey: null,
      alarm: null,
    });
  }

  private loadData(): void {
    forkJoin({
      interactionOptions: this.taskService.getFieldOption(),
      users: this.userService.getActiveUsers(),
      currentUser: this.session.getCurrentUser(),
    })
      .pipe(
        catchError(async () => {
          this.notification.show('load', 'error');
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((data) => {
        if (!data) return;

        const { categories, results, priorities } = data.interactionOptions;
        categories.unshift(this.noneValue);
        results.unshift(this.noneValue);
        priorities.unshift(this.noneValue);

        this.configuration = {
          options: {
            categories: categories ?? [this.noneValue],
            results: results ?? [this.noneValue],
            priorities: priorities ?? [this.noneValue],
          },
          users: data.users ?? [],
        };

        this.form?.patch({
          assignedUser: data.currentUser.id,
          priority: this.noneValue.id,
          category: this.noneValue.id,
          result: this.noneValue.id,
        });
      });
    if (!this.configuration) return;
  }
}
